import React from 'react';
import useSwr, { KeyedMutator } from 'swr';
import { usePagination, PaginationHandler, SortHandler, SortRule, usePaginationValues } from '@economic/taco';
import { CollectionEnvelope } from '~/types/internal-api';
import internalApiClient from '../util/api/internalApiClient';
import usePagePreferences from './usePagePreferences';
import useSavedPreferencesWithCustomKey from './useSavedPreferencesWithCustomKey';

export const fetcher = (url: string) => internalApiClient.get(url).then(response => response.data);

export const getQueryString = (endpoint: string): URL => {
    return new URL(window.location.origin + endpoint);
};

export const getDefaultSortRules = (endpoint: string, prefs: Partial<CollectionQueryPreferences>) => {
    if (prefs?.sortRules) {
        return prefs.sortRules;
    }

    const url = getQueryString(endpoint);
    const sort = url.searchParams.get('sort');

    if (sort) {
        return sort.split(',').map(rule => ({ accessor: rule.replace('-', ''), desc: rule.startsWith('-') }));
    }

    return [];
};

export const getDefaultPagination = (endpoint: string, prefs: Partial<CollectionQueryPreferences>) => {
    const url = getQueryString(endpoint);
    const pageIndex = url.searchParams.get('skippages');
    const pageSize = prefs?.pageSize || url.searchParams.get('pagesize');

    return { pageIndex: pageIndex ? Number(pageIndex) : undefined, pageSize: pageSize ? Number(pageSize) : undefined };
};

const MAX_PAGE_SIZE = 500;

export const createPath = <TType = unknown>(
    endpoint: string,
    sortRules: SortRule<TType>[],
    pagination: Pick<usePaginationValues, 'pageSize' | 'pageIndex'>,
    returnAll: boolean
) => {
    // build query url from provided endpoint and state
    const url = new URL(window.location.origin + endpoint);

    if (sortRules.length) {
        url.searchParams.set('sort', sortRules.map(rule => `${rule.desc ? '-' : ''}${rule.accessor}`).join(','));
    }

    const pageSize = returnAll ? MAX_PAGE_SIZE : pagination.pageSize;
    url.searchParams.set('pagesize', String(pageSize));
    url.searchParams.set('skippages', String(pagination.pageIndex));

    return url.pathname + url.search;
};

export interface CollectionQueryPreferences<TType = unknown> {
    pageSize: number;
    sortRules: SortRule<TType>[];
}

export interface Pagination {
    // references the length of the dataset without any pagination applied
    length: number;
    onPaginate: PaginationHandler;
    pageIndex: number;
    pageSize: number;
}

export interface Sorting<TType = unknown> {
    onSort: SortHandler<TType>;
    sortRules: SortRule<TType>[];
}

export interface CollectionQuery<TType = unknown> {
    data: CollectionEnvelope<TType>['data'];
    error?: Error;
    loading: boolean;
    refetch: () => Promise<void>;
    mutate: KeyedMutator<CollectionEnvelope<TType>>;
    // references the length of the dataset without any filtering applied
    totalUnfilteredLength: number;
    sorting: Sorting<TType>;
    // pagination is undefined when returnAll is passed
    pagination?: Pagination;
    path: string;
}

export const useCollectionQuery = <TType = unknown>(
    endpoint: string,
    returnAll = false,
    saveKey = '',
    defaultPagination: { pageIndex: number; pageSize: number } | null = null,
    defaultSortRules: SortRule<TType>[] | null = null
): CollectionQuery<TType> => {
    const [endpointPrefs, setEndpointPrefs] = saveKey
        ? useSavedPreferencesWithCustomKey(saveKey)
        : usePagePreferences<CollectionQueryPreferences>();
    const paginationDefaults = React.useMemo(
        () => defaultPagination || getDefaultPagination(endpoint, endpointPrefs),
        [endpointPrefs]
    );

    // initialise sort and pagination state from passed parameters (defaultSortRules & defaultPagination)
    // or from the endpoint parameter, incase sorting and pagination are set as defaults or are saved in page prefs
    const [sortRules, setSortRules] = React.useState<SortRule<TType>[]>(
        defaultSortRules || getDefaultSortRules(endpoint, endpointPrefs)
    );
    const pagination = usePagination(paginationDefaults.pageIndex, paginationDefaults.pageSize);

    // reset page index when endpoint (filters) changes to prevent returning empty data
    // reset page index will be skipped if defaultPagination parameter is set
    React.useEffect(() => {
        if (!defaultPagination) {
            pagination.setPageIndex(0);
        }
    }, [endpoint]);

    // build query path from provided endpoint and state
    const path = React.useMemo(
        () => createPath(endpoint, sortRules, pagination, returnAll),
        [endpoint, pagination, returnAll, sortRules]
    );

    // perform query
    const response = useSwr<CollectionEnvelope<TType>>(path, fetcher, {
        revalidateOnFocus: false, // causes strange double queries when unmounting
    });

    // if the original query is returning all, we don't want pagination
    const shouldPaginate = !returnAll;

    return {
        data: response.data?.data || [],
        error: response.error,
        loading: response.isValidating,
        refetch: () => response.mutate() as Promise<void>,
        mutate: response.mutate,
        totalUnfilteredLength: response.data?.totalUnfilteredCount || 0,
        sorting: {
            onSort: (nextSortRules = []) => {
                setSortRules(nextSortRules);
                // save rules to local storage
                setEndpointPrefs({ sortRules: nextSortRules });
            },
            sortRules,
        },
        pagination: shouldPaginate
            ? {
                  length: response.data?.pagination.count || 0,
                  onPaginate: (nextPageIndex, nextPageSize) => {
                      if (nextPageIndex !== pagination.pageIndex) {
                          pagination.setPageIndex(nextPageIndex);
                      }

                      if (nextPageSize !== pagination.pageSize) {
                          pagination.setPageSize(nextPageSize);
                          // save page size to local storage
                          setEndpointPrefs({ pageSize: nextPageSize });
                      }
                  },
                  pageIndex: response.data?.pagination.skipPages || pagination.pageIndex,
                  pageSize: response.data?.pagination.pageSize || pagination.pageSize,
              }
            : undefined,
        path,
    };
};
